import React, { useEffect, useState } from 'react';
import { Col, Row } from 'reactstrap';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';
import { Link, useNavigate } from 'react-router-dom';
import CommonButton from '../CommonButton';
import AuctionCountdown from './AuctionCountdown';
import locationIcon from '../../../assets/img/icons/location_on.svg';
import shareIcon from '../../../assets/img/icons/share.svg';
import favoriteIcon from '../../../assets/img/icons/favorite.svg';
import favoriteRedIcon from '../../../assets/img/icons/favorite-red.svg';
import registrationPlateIcon from '../../../assets/img/icons/registration-plate.svg';
import brushIcon from '../../../assets/img/icons/brush.svg';
import autoTransmissionIcon from '../../../assets/img/icons/auto_transmission.svg';
import calendarMonthIcon from '../../../assets/img/icons/calendar-month.svg';
import doorsIcon from '../../../assets/img/icons/doors.svg';
import fuelIcon from '../../../assets/img/icons/fuel.svg';
import milesIcon from '../../../assets/img/icons/miles.svg';
import personIcon from '../../../assets/img/icons/person.svg';
import variantIcon from '../../../assets/img/icons/variant.svg';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import { AuctionListTemplateContextType, ViewType } from './AuctionListTemplate';
import {
  addFavouriteAuction,
  deleteFavouriteAuction,
} from '../../../settings/SettingsActions';
import { placeBid } from '../../../search/SearchActions';
import useDefaultBidLocationId from '../../hooks/useDefaultBidLocationId';
import { useIsAllLocationsSelected } from '../../hooks/useLocationIdCookie';
import SearchBidModal from './bidModals/SearchBidModal';
import AuctionImageCarousel from './AuctionImageCarousel';
import useAuctionCard from '../../hooks/useAuctionCard';
import { replaceRouteParams } from '../../helpers/replaceRouteParams';
import RouterPaths from '../../../constants/RouterPaths';
import { AuctionStatus } from '../../../constants/AuctionStatus';
import ShareAuctionBox from '../ShareAuctionBox';

function AuctionListItem({
  item,
  viewType,
  isFavourite,
  isAllLocationsScope,
  refreshAuctionListCallback,
  context,
  listType,
}) {
  const isMobile = useMediaQuery('(max-width: 767px)');
  const isTablet = useMediaQuery('(min-width: 768px) and (max-width: 1424px)');
  const isDesktop = useMediaQuery('(min-width: 1425px)');

  const [isFavouriteAuction, setIsFavouriteAuction] = useState(isFavourite);
  const [showBidModal, setShowBidModal] = useState(false);
  const [currentBidAuction, setCurrentBidAuction] = useState(null);

  const navigate = useNavigate();

  useEffect(() => {
    setIsFavouriteAuction(isFavourite);
  }, [isFavourite]);

  // Safely extract nested fields; backend may return nulls for some sub-objects
  const vehicle = item?.vehicle || {};
  const registrationPlate = vehicle?.registrationPlate || 'Not defined';
  const year = vehicle?.year || 'Not defined';
  const mileage = vehicle?.mileage;
  const transmission = vehicle?.transmission || 'Not defined';
  const carType = vehicle?.carType || 'Not defined';
  const fuel = vehicle?.fuel || 'Not defined';
  const doors = vehicle?.doors;
  const colour = vehicle?.colour || 'Not defined';
  const previousKeepersInTotal = vehicle?.previousKeepersInTotal;
  const derivative = vehicle?.derivative || 'Not defined';
  const vehicleModel = vehicle?.vehicleModel || {};
  const makeName = vehicleModel?.makeName || 'Not defined';
  const name = vehicleModel?.name || 'Not defined';
  const vehicleMedia = vehicle?.vehicleMedia || {};
  const photos = vehicleMedia?.photos || [];
  const videos = vehicleMedia?.videos || [];
  const vehicleHighlightedInfo = vehicle?.vehicleHighlightedInfo || '';
  const locationObj = item?.location || {};
  const city = locationObj?.city || 'Not defined';
  const county = locationObj?.county || 'Not defined';

  const carSpecificationsList = [
    ...(!isMobile && viewType === ViewType.LIST
      ? [
          {
            icon: registrationPlateIcon,
            value: registrationPlate,
          },
        ]
      : []),
    {
      icon: calendarMonthIcon,
      value: year,
    },
    {
      icon: milesIcon,
      value: mileage ? `${mileage} miles` : 'Not defined',
    },
    {
      icon: autoTransmissionIcon,
      value: transmission,
    },
    ...(!isMobile && viewType === ViewType.LIST
      ? [
          {
            icon: variantIcon,
            value: carType,
          },
        ]
      : []),
    {
      icon: fuelIcon,
      value: fuel,
    },
    ...(isDesktop && viewType === ViewType.LIST
      ? [
          {
            icon: personIcon,
            value: Number(previousKeepersInTotal)
              ? `${previousKeepersInTotal} owner${previousKeepersInTotal > 1 ? 's' : ''}`
              : 'Not defined',
          },
        ]
      : []),
    ...(isDesktop && viewType === ViewType.LIST
      ? [
          {
            icon: brushIcon,
            value: colour,
          },
        ]
      : []),
    ...(isDesktop && viewType === ViewType.LIST
      ? [
          {
            icon: doorsIcon,
            value: doors ? `${doors} doors` : 'Not defined',
          },
        ]
      : []),
  ];

  const formatGBP = (amountInPence) => {
    const isNum = typeof amountInPence === 'number' && Number.isFinite(amountInPence);
    const pounds = isNum ? Math.round(amountInPence / 100) : 0;
    const grouped = pounds.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return `£${grouped}`;
  };

  const dispatch = useDispatch();
  const memberId = useSelector((state) => state.auth.get('id'));
  const organizationLocationId = useDefaultBidLocationId();
  const isAllLocationsSelected = useIsAllLocationsSelected();

  const handleBidModal = (item) => {
    setCurrentBidAuction(item);
    setShowBidModal(true);
  };

  const toggleBidModal = () => {
    setShowBidModal(false);
  };

  const handlePlaceBid = (amount) => {
    dispatch(placeBid(currentBidAuction?.id, amount * 100, null, organizationLocationId));
  };

  const baseDetailsPath = RouterPaths?.[`${listType}_DETAILS`] || RouterPaths?.SEARCH_DETAILS;
  const detailsPath = replaceRouteParams(baseDetailsPath, {
    id: item.id,
    isFavourite,
  });

  const handleNavigateToDetails = () => {
    navigate(detailsPath);
  };

  const { priceCaption, displayPriceInPence, primaryAction, status, bidContext } =
    useAuctionCard(item, memberId, {
      onBid: handleNavigateToDetails,
      onBuy: handleNavigateToDetails,
    });

  const formattedPrice =
    typeof displayPriceInPence === 'number' && !Number.isNaN(displayPriceInPence)
      ? formatGBP(displayPriceInPence)
      : '-';

  const bidBar = (() => {
    if (!bidContext.show || !bidContext.message) return null;
    const barClass =
      bidContext.variant === 'green'
        ? 'auction-list-item__bid-bar__green'
        : 'auction-list-item__bid-bar__red';
    return (
      <div className={`auction-list-item__bid-bar ${barClass}`}>
        <span>{bidContext.message}</span>
      </div>
    );
  })();

  const _handleToggleFavourite = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    const wasFavourite =
      isFavouriteAuction || context === AuctionListTemplateContextType.FAVOURITE_OFFERS;
    // optimistic update
    setIsFavouriteAuction((prevState) => !prevState);

    const body = { memberId, auctionId: item.id, organizationLocationId };
    try {
      let res;
      if (wasFavourite) {
        res = await dispatch(
          deleteFavouriteAuction(
            isAllLocationsScope || isAllLocationsSelected
              ? { ...body, organizationLocationId: null }
              : body,
          ),
        );
      } else {
        res = await dispatch(
          addFavouriteAuction(
            isAllLocationsSelected ? { ...body, organizationLocationId: null } : body,
          ),
        );
      }
      // if non-200, revert optimistic change
      if (!res || res.status !== 200) {
        setIsFavouriteAuction((prev) => !prev);
      }
    } catch (err) {
      // revert on error
      setIsFavouriteAuction((prev) => !prev);
    } finally {
      if (refreshAuctionListCallback) {
        refreshAuctionListCallback();
      }
    }
  };

  const isOwnAuction =
    (Boolean(item?.sellerId) && memberId === String(item.sellerId)) ||
    (Boolean(item?.seller?.id) && memberId === String(item?.seller?.id));

  const renderActionButtons = () => {
    const isPublished = item?.status === AuctionStatus.PUBLISHED;
    if (!isPublished) return null;
    const shareUrl = `${window.location.origin}/auction/${item?.id}`;
    return (
      <div className="d-flex">
        <button
          type="button"
          id={`auctionListItem-${item?.id}`}
          className="auction-list-item__action-btn"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
        >
          <img src={shareIcon} alt="Social Media Icon" />
        </button>
        <ShareAuctionBox
          target={`auctionListItem-${item?.id}`}
          shareUrl={shareUrl}
          uncontrolledTooltipClassName="auction-list-item__share-box-tooltip"
        />
        {!isOwnAuction && item?.status === AuctionStatus.PUBLISHED && (
          <button
            type="button"
            className={`auction-list-item__action-btn ${isFavouriteAuction || context === AuctionListTemplateContextType.FAVOURITE_OFFERS ? 'auction-list-item__action-btn--red' : ''}`}
            onClick={(e) => _handleToggleFavourite(e)}
          >
            {isFavouriteAuction ||
            context === AuctionListTemplateContextType.FAVOURITE_OFFERS ? (
              <img src={favoriteRedIcon} alt="Red heart icon" />
            ) : (
              <img src={favoriteIcon} alt="Black heart icon" />
            )}
          </button>
        )}
      </div>
    );
  };

  const renderVehicleName = () => (
    <Row className="auction-list-item__car-info">
      <span>
        {makeName} {name}
      </span>
      <span>{derivative}</span>
    </Row>
  );

  const renderBadges = () => (
    <div
      className={`d-flex flex-column gap-1 ${viewType !== ViewType.GRID ? 'flex-md-row gap-md-2' : ''}`}
    >
      <div
        className={`auction-list-item__badge align-self-start order-1  ${viewType !== ViewType.GRID ? 'order-md-0' : ''} `}
      >
        <img src={locationIcon} alt="Social Media Icon" />
        <span>
          {city}, {county}
        </span>
      </div>
      {Boolean(vehicleHighlightedInfo) && (
        <div
          className={`auction-list-item__badge auction-list-item__badge--special align-self-start order-0 ${viewType !== ViewType.GRID ? ' order-md-1' : ''} `}
        >
          <span>{vehicleHighlightedInfo}</span>
        </div>
      )}
    </div>
  );

  const getCarouselWidth = () => {
    if (viewType === ViewType.LIST) return 320;
    if (viewType === ViewType.GRID) return '100%';
    return '100%';
  };

  return (
    <>
      <div
        role="button"
        tabIndex="0"
        className={classNames(
          viewType === ViewType.GRID && 'auction-list-item--grid',
          'auction-list-item mb-4 m-0 p-0',
        )}
      >
        <div
          className={`auction-list-item__media-box position-relative d-flex justify-content-center align-items-center ${isMobile || viewType === ViewType.GRID ? 'auction-list-item__media-box--grid' : ''}`}
        >
          <Link
            to={detailsPath}
            target="_blank"
            rel="noopener noreferrer"
            style={{ display: 'block', width: getCarouselWidth(), height: 280 }}
          >
            <AuctionImageCarousel
              photos={photos}
              videos={videos}
              height={280}
              width={getCarouselWidth()}
              viewType={viewType}
            />
          </Link>
          {(isMobile || viewType === ViewType.GRID) && (
            <div className="position-absolute top-0 end-0 p-2">{renderActionButtons()}</div>
          )}
        </div>
        <Link
          to={detailsPath}
          target="_blank"
          rel="noopener noreferrer"
          className={classNames(
            'auction-list-item__content-box',
            viewType === ViewType.GRID && 'auction-list-item__content-box__grid',
          )}
        >
          <div
            className={`d-flex justify-content-between mb-2  text-start ${viewType === ViewType.LIST ? 'mb-md-2' : 'mb-md-4'}`}
          >
            {isMobile || viewType === ViewType.GRID ? renderVehicleName() : renderBadges()}

            {!isMobile && viewType !== ViewType.GRID && renderActionButtons()}
          </div>

          <Row className="auction-list-item-row">
            <Col
              sm={12}
              md={viewType === ViewType.GRID ? 12 : 7}
              xl={viewType === ViewType.GRID ? 12 : 9}
              className="auction-list-item__info-box"
            >
              {isMobile || viewType === ViewType.GRID ? renderBadges() : renderVehicleName()}
              <div className="auction-list-item__specification-list">
                {carSpecificationsList.map((spec, idx) => (
                  <div
                    // eslint-disable-next-line react/no-array-index-key
                    key={`${spec.icon}-${spec.value}-${idx}`}
                    className={classNames(
                      viewType === ViewType.GRID &&
                        'auction-list-item__specification-list-item__grid',
                      'auction-list-item__specification-list-item',
                    )}
                  >
                    <div>
                      <img src={spec.icon} alt="i" />
                    </div>
                    <span>{spec.value}</span>
                  </div>
                ))}
              </div>
              {bidBar}
            </Col>
            <Col
              sm={12}
              md={viewType === ViewType.GRID ? 12 : 5}
              xl={viewType === ViewType.GRID ? 12 : 3}
              className="mt-3"
            >
              <div className="auction-list-item__bid-info">
                {status && <span className={status.className}>{status.text}</span>}
                <div className={`d-flex flex-row flex-wrap `}>
                  <div
                    className={`auction-list-item__price-info ${isMobile || viewType === ViewType.GRID ? 'auction-list-item__info--grid' : ''}`}
                  >
                    <span>{formattedPrice}</span>
                    <span>{priceCaption}</span>
                  </div>
                  <div
                    className={`auction-list-item__time-info ${isMobile || viewType === ViewType.GRID ? 'auction-list-item__info--grid' : ''}`}
                  >
                    <AuctionCountdown item={item} context={context} />
                  </div>
                  <div className="w-100 mt-2">
                    {/* // TODO: add logic for buy now button */}
                    <CommonButton
                      type="submit"
                      label={primaryAction.label}
                      className="auction-place-bid-box__send-button text-sm"
                      variant={primaryAction.variant}
                      handleClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        e.nativeEvent?.stopImmediatePropagation?.();
                        if (primaryAction.onClick) primaryAction.onClick(e);
                      }}
                      icon={primaryAction.icon}
                      iconAlt={primaryAction.iconAlt}
                      iconLeft
                      disabled={primaryAction.disabled}
                    />
                  </div>
                </div>
              </div>
            </Col>
          </Row>
        </Link>
      </div>
      {showBidModal && (
        <SearchBidModal
          toggleShow={toggleBidModal}
          item={currentBidAuction}
          handlePlaceBid={handlePlaceBid}
        />
      )}
    </>
  );
}

export default AuctionListItem;
